import React, { useEffect, useState, useCallback } from "react";
import {
  Button, Input, Typography, Spin, Toast, Tag, Space, Modal, Switch, TextArea,
} from "@douyinfe/semi-ui";
import { IconSearch, IconDelete, IconRefresh, IconEdit } from "@douyinfe/semi-icons";
import { ApiClient } from "../../api/ApiClient";

const { Text, Title } = Typography;
const api = ApiClient.getDefault();

interface CacheGroup {
  id: string;
  key: string;
  count: number;
}

interface CacheKey {
  key: string;
  expire: number;
}

const formatExpire = (seconds: number): string => {
  if (seconds < 0) return "永不过期";
  if (seconds < 60) return `${seconds}秒`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}分${seconds % 60 ? seconds % 60 + "秒" : ""}`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}时${Math.floor((seconds % 3600) / 60)}分`;
  return `${Math.floor(seconds / 86400)}天${Math.floor((seconds % 86400) / 3600)}时`;
};

const expireColor = (seconds: number): any => {
  if (seconds < 0) return "green";
  if (seconds < 300) return "red";
  if (seconds < 3600) return "orange";
  return "blue";
};

export default function RedisManager() {
  const [switchOn, setSwitchOn] = useState(true);
  const [groups, setGroups] = useState<CacheGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [keys, setKeys] = useState<CacheKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keySearch, setKeySearch] = useState("");

  const [selectedKey, setSelectedKey] = useState<string>("");
  const [cacheValue, setCacheValue] = useState("");
  const [valueLoading, setValueLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [editExpire, setEditExpire] = useState(3600);
  const [saving, setSaving] = useState(false);

  /* ---------- 缓存开关 ---------- */

  const loadSwitch = useCallback(async () => {
    try {
      const resp = await api.getCacheSwitchOn();
      setSwitchOn(resp.data);
    } catch {}
  }, []);

  const toggleSwitch = async (checked: boolean) => {
    try {
      const resp = await api.setCacheSwitchOn();
      if (resp.code === "ok") setSwitchOn(checked);
    } catch {}
  };

  /* ---------- 缓存组列表 ---------- */

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const resp = await api.request<{ records: { id: string; key: string; expire: number }[] }>(
        "/redisGroupManagers", "GET", { page: 1, size: 100 }
      );
      const records = resp.data?.records || [];
      setGroups(records.map(r => ({ id: r.id, key: r.key, count: r.expire || 0 })));
    } catch {} finally { setGroupsLoading(false); }
  }, []);

  /* ---------- 缓存 key 列表 ---------- */

  const loadKeys = useCallback(async (groupKey: string, search?: string) => {
    setKeysLoading(true);
    setKeys([]);
    setSelectedKey("");
    try {
      const params: Record<string, any> = { page: 1, size: 500, groupKey };
      if (search) params.key = search;
      const resp = await api.request<{ records: { key: string; expire: number }[] }>(
        "/redisManagers", "GET", params
      );
      setKeys((resp.data?.records || []).map(r => ({ key: r.key, expire: r.expire })));
    } catch {} finally { setKeysLoading(false); }
  }, []);

  /* ---------- 缓存值 ---------- */

  const loadValue = async (key: string) => {
    setSelectedKey(key);
    setValueLoading(true);
    setEditing(false);
    try {
      const resp = await api.getCacheValue(key);
      setCacheValue(resp.data || "");
    } catch {} finally { setValueLoading(false); }
  };

  /* ---------- 保存编辑 ---------- */

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateCacheValue(selectedKey, editValue, editExpire);
      Toast.success("更新成功");
      setCacheValue(editValue);
      setEditing(false);
      loadKeys(selectedGroup, keySearch || undefined);
    } catch (e: any) {
      Toast.error(e?.message || "更新失败");
    } finally { setSaving(false); }
  };

  /* ---------- 删除 ---------- */

  const handleDeleteKey = (key: string) => {
    Modal.confirm({
      title: "删除缓存",
      content: `确定删除 ${key}？`,
      onOk: async () => {
        try {
          await api.deleteCacheByKey(key);
          Toast.success("已删除");
          if (selectedKey === key) { setSelectedKey(""); setCacheValue(""); }
          loadKeys(selectedGroup, keySearch || undefined);
          loadGroups();
        } catch (e: any) { Toast.error(e?.message || "删除失败"); }
      },
    });
  };

  const handleDeleteGroup = (group: CacheGroup) => {
    Modal.confirm({
      title: "清空缓存组",
      content: `确定清空 ${group.key} 下的所有缓存（${group.count} 条）？`,
      onOk: async () => {
        try {
          await api.deleteCacheGroupByKey(group.key);
          Toast.success("已清空");
          if (selectedGroup === group.key) {
            setSelectedGroup("");
            setKeys([]);
            setSelectedKey("");
            setCacheValue("");
          }
          loadGroups();
        } catch (e: any) { Toast.error(e?.message || "清空失败"); }
      },
    });
  };

  /* ---------- 选择组 ---------- */

  const selectGroup = (groupKey: string) => {
    setSelectedGroup(groupKey);
    setKeySearch("");
    loadKeys(groupKey);
  };

  /* ---------- 搜索 key ---------- */

  const handleSearchKey = () => {
    if (selectedGroup) loadKeys(selectedGroup, keySearch || undefined);
  };

  /* ---------- 开始编辑 ---------- */

  const startEdit = () => {
    setEditValue(cacheValue);
    setEditExpire(3600);
    setEditing(true);
  };

  /* ---------- 初始化 ---------- */

  useEffect(() => { loadSwitch(); loadGroups(); }, [loadSwitch, loadGroups]);

  /* ---------- 格式化 JSON ---------- */

  const formatJson = (s: string) => {
    if (!s) return "";
    try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
  };

  const isJson = (s: string) => { try { JSON.parse(s); return true; } catch { return false; } };

  /* ==================== 渲染 ==================== */

  return (
    <div style={{ display: "flex", height: "100%", background: "var(--semi-color-bg-1)" }}>
      {/* 左栏：缓存组 */}
      <div style={{ width: 320, borderRight: "1px solid var(--semi-color-border)", background: "#fff", display: "flex", flexDirection: "column" }}>
        {/* 头部 */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--semi-color-border)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <Text strong style={{ fontSize: 15 }}>缓存管理</Text>
            <Space>
              <Switch checked={switchOn} checkedText="开" uncheckedText="关" size="small" onChange={toggleSwitch} />
              <Button size="small" icon={<IconRefresh />} onClick={() => { loadGroups(); if (selectedGroup) loadKeys(selectedGroup, keySearch || undefined); }} />
            </Space>
          </div>
          <Text size="small" type="tertiary">缓存开关：{switchOn ? "已启用" : "已关闭"}</Text>
        </div>

        {/* 组列表 */}
        <Spin spinning={groupsLoading}>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {groups.map(g => (
              <div key={g.key} onClick={() => selectGroup(g.key)} style={{
                padding: "10px 16px", cursor: "pointer",
                background: selectedGroup === g.key ? "var(--semi-color-primary-light-default)" : "transparent",
                borderLeft: selectedGroup === g.key ? "3px solid var(--semi-color-primary)" : "3px solid transparent",
                borderBottom: "1px solid var(--semi-color-border)",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ minWidth: 0 }}>
                  <Text strong size="small" ellipsis={{ showTooltip: true }} style={{ display: "block", maxWidth: 200 }}>
                    {g.key}
                  </Text>
                  <Tag size="small" style={{ marginTop: 2 }}>{g.count} 条</Tag>
                </div>
                <Button size="small" type="danger" theme="borderless" icon={<IconDelete />}
                  onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g); }} />
              </div>
            ))}
          </div>
        </Spin>
      </div>

      {/* 中栏：缓存 key 列表 */}
      <div style={{ width: 380, borderRight: "1px solid var(--semi-color-border)", background: "#fff", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--semi-color-border)", flexShrink: 0 }}>
          {selectedGroup ? (
            <>
              <Text strong size="small" ellipsis={{ showTooltip: true }} style={{ display: "block", marginBottom: 8 }}>
                {selectedGroup}
              </Text>
              <div style={{ display: "flex", gap: 8 }}>
                <Input placeholder="搜索 key" value={keySearch} onChange={setKeySearch}
                  onEnterPress={handleSearchKey} style={{ flex: 1 }} prefix={<IconSearch />} size="small" />
                <Button size="small" onClick={handleSearchKey}>搜索</Button>
              </div>
            </>
          ) : (
            <Text type="tertiary" size="small">选择左侧缓存组</Text>
          )}
        </div>

        <Spin spinning={keysLoading}>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {keys.map(k => (
              <div key={k.key} onClick={() => loadValue(k.key)} style={{
                padding: "8px 16px", cursor: "pointer", fontSize: 12,
                background: selectedKey === k.key ? "#e6f7ff" : "transparent",
                borderLeft: selectedKey === k.key ? "3px solid var(--semi-color-primary)" : "3px solid transparent",
                borderBottom: "1px solid var(--semi-color-border)",
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Text ellipsis={{ showTooltip: true }} style={{ fontSize: 12, flex: 1, marginRight: 8 }}>{k.key}</Text>
                  <Tag size="small" color={expireColor(k.expire)}>{formatExpire(k.expire)}</Tag>
                </div>
              </div>
            ))}
            {selectedGroup && keys.length === 0 && !keysLoading && (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Text type="tertiary" size="small">无缓存记录</Text>
              </div>
            )}
          </div>
        </Spin>
      </div>

      {/* 右栏：缓存值详情 */}
      <div style={{ flex: 1, background: "var(--semi-color-bg-0)", display: "flex", flexDirection: "column", minWidth: 0 }}>
        {selectedKey ? (
          <>
            {/* 详情头部 */}
            <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--semi-color-border)", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Text strong ellipsis={{ showTooltip: true }} style={{ fontSize: 13, maxWidth: "70%" }}>{selectedKey}</Text>
                <Space>
                  <Button size="small" icon={<IconEdit />} onClick={startEdit}>编辑</Button>
                  <Button size="small" type="danger" icon={<IconDelete />} onClick={() => handleDeleteKey(selectedKey)}>删除</Button>
                </Space>
              </div>
            </div>

            {/* 值内容 */}
            <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
              {valueLoading ? (
                <div style={{ textAlign: "center", padding: 60 }}><Spin /></div>
              ) : editing ? (
                <div>
                  <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                    <Text size="small" type="tertiary">过期时间(秒)：</Text>
                    <Input type="number" value={editExpire} onChange={(v: string) => setEditExpire(Number(v))}
                      style={{ width: 120 }} size="small" />
                    <Button size="small" onClick={() => setEditExpire(3600)}>1小时</Button>
                    <Button size="small" onClick={() => setEditExpire(86400)}>1天</Button>
                    <Button size="small" onClick={() => setEditExpire(-1)}>永不过期</Button>
                  </div>
                  <TextArea value={editValue} onChange={setEditValue} rows={20}
                    style={{ fontFamily: "monospace", fontSize: 12 }} />
                  <Space style={{ marginTop: 12 }}>
                    <Button theme="solid" onClick={handleSave} loading={saving}>保存</Button>
                    <Button onClick={() => setEditing(false)}>取消</Button>
                  </Space>
                </div>
              ) : (
                <div>
                  {isJson(cacheValue) && (
                    <Tag color="blue" size="small" style={{ marginBottom: 8 }}>JSON</Tag>
                  )}
                  <pre style={{
                    background: "#1e1e1e", color: "#d4d4d4", padding: 16, borderRadius: 8,
                    fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-all",
                    maxHeight: "calc(100vh - 200px)", overflow: "auto",
                  }}>
                    {isJson(cacheValue) ? formatJson(cacheValue) : cacheValue || "(空)"}
                  </pre>
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <Text type="tertiary">选择缓存 key 查看详情</Text>
          </div>
        )}
      </div>
    </div>
  );
}
